import { useEffect, useRef, useState } from "react";
import { supabase } from "../lib/supabase";
import {
  MEASUREMENT_FIELDS, fieldLabel, fieldUnit, measurementPayload, savedSentence,
  type MeasurementField,
} from "../lib/coachMeasurement";

/**
 * `Update results` — THE COACH TYPES THEIR CLIENT'S BODY MEASUREMENTS IN.
 *
 * 🔴 HIS WORD, 2026-09-19: *"yes a coach should be able to type a client's own body measurements
 * in"*, and Everfit's own shape read live at depth 3 (`EVERFIT-MAP` §2c-ter): a date picker at the
 * top right, ONE ROW PER METRIC — label left, a number input with the UNIT AS A SUFFIX INSIDE THE
 * FIELD — and a single ENABLED `Save`.
 *
 * ⚠️ THE SAVE IS ENABLED FROM THE FIRST FRAME, WHICH IS THEIRS AND IS ALSO OURS ANYWAY. §1d:
 * a disabled submit *"is the first place in the map where copying them would make ours worse"* —
 * it cannot say why. Pressing it with nothing typed answers *"Type at least one measurement."*
 *
 * ⚠️ THE WRITE IS A NARROW STRUCT AND THE REASON IS A LIVE MEASUREMENT, NOT A STYLE. The policy
 * `body_measurements_coach_insert` refuses a coach's row that carries a photo path; its sibling
 * `..._coach_update` refuses nothing of the kind. Rehearsed against the live database and rolled
 * back (`~/ht-logs/day-0919/coachwrite/`): the coach's INSERT with a photo path is **42501**, the
 * coach's UPDATE setting one touches **1 row**. So this component only ever INSERTS, and it
 * insists on `measurementPayload`, whose key set is `user_id`, `measured_at` and filled numbers.
 * ⚠️ **IT NEVER UPDATES AN EXISTING ROW** — correcting a reading is the half the policy cannot yet
 * be trusted with, and an "add" that silently overwrote yesterday's entry would be the destructive
 * act wearing an additive name.
 */
export function UpdateResultsWindow({ clientId, todayKey, open, onClose, onSaved }: {
  readonly clientId: string;
  /** The day the form opens on — passed in so the caller owns "today", never a second clock. */
  readonly todayKey: string;
  readonly open: boolean;
  readonly onClose: () => void;
  /** The panel refetches: it is reading the same table this just wrote to. */
  readonly onSaved: () => void;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const firstRef = useRef<HTMLInputElement>(null);
  const [dayKey, setDayKey] = useState(todayKey);
  const [values, setValues] = useState<Partial<Record<MeasurementField, string>>>({});
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [said, setSaid] = useState<string | null>(null);

  useEffect(() => {
    const el = dialogRef.current;
    if (el === null) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);

  /* Focus lands on the DATE, which is the field a coach changes least often and reads first —
     and never on `Save`, which is the browser's own choice and announces the destination before
     the form. An effect keyed on `open` runs after the commit, which is the only ordering that
     can be relied on (the note in `DiscardWindow` carries the measurement behind that). */
  useEffect(() => { if (open) firstRef.current?.focus(); }, [open]);

  /* ⚠️ THE FORM EMPTIES ON EVERY OPEN. A coach who saved a weight, closed, and came back to add a
     waist must not find last time's numbers still typed in — they would be saved again under a new
     date, doubling a reading nobody took twice. */
  useEffect(() => {
    if (!open) return;
    setValues({});
    setDayKey(todayKey);
    setProblem(null);
    setSaid(null);
  }, [open, todayKey]);

  async function save() {
    const built = measurementPayload(clientId, dayKey, values);
    if (!built.ok) { setProblem(built.why); setSaid(null); return; }
    setSaving(true); setProblem(null);
    const { error } = await supabase.from("body_measurements").insert(built.row);
    setSaving(false);
    if (error !== null) {
      /* The server's own words. A rewritten message is a second, worse copy of an error we did not
         generate — and here it is usually the policy or a CHECK constraint, both worth seeing. */
      setProblem(error.message);
      return;
    }
    setSaid(savedSentence(built.filled, dayKey));
    setValues({});
    onSaved();
  }

  return (
    <dialog ref={dialogRef} className="ht-window measure-window"
            aria-labelledby="update-results-title"
            onCancel={(e) => { e.preventDefault(); onClose(); }}>
      <div className="ht-window-frame">
        <header className="ht-window-head">
          <div className="ht-window-titles">
            {/* ⚠️ NOT THE CLIENT'S NAME, AND NOT A STAND-IN FOR IT. `ClientResults` holds only a
                `clientId`, so a name here would have to be plumbed through two components or
                invented — and "This client" on a window that writes somebody's body is worse than
                saying nothing. The page underneath carries their name in its own `h1`, and this
                window is drawn over it. */}
            <p className="page-eyebrow">Body metrics</p>
            {/* No class of its own: `.builder-window-title` exists because that heading is focused
                programmatically and needs `fit-content` so its ring hugs the words. This one is
                never focused — the DATE takes focus — so a class here would be a name with no
                rule behind it, which is what the ruler caught. */}
            <h2 className="ht-title" id="update-results-title">Update results</h2>
          </div>
          <button type="button" className="ht-button ht-button-quiet ht-window-close"
                  onClick={onClose}>Close</button>
        </header>

        <div className="ht-window-body measure-body">
          {/* THE DATE, TOP RIGHT — theirs, and it is the one field that is never optional. */}
          <div className="ht-field measure-date">
            <label className="ht-field-label" htmlFor="measure-date">Date</label>
            <input id="measure-date" ref={firstRef} className="ht-input" type="date"
                   value={dayKey} max={todayKey}
                   onChange={(e) => { setDayKey(e.target.value); setSaid(null); }} />
          </div>

          {/* ONE ROW PER METRIC. ⚠️ THE UNIT IS DRAWN AS A SUFFIX INSIDE THE FIELD — theirs — and
              it is `aria-hidden` with the unit also in the label's accessible name, because a
              decoration beside an input is not a label and a screen reader would otherwise read
              "Weight" and never "kg". */}
          <ul className="measure-fields">
            {MEASUREMENT_FIELDS.map((f) => (
              <li key={f} className="measure-field">
                <label className="ht-field-label measure-field-label" htmlFor={`m-${f}`}>
                  {fieldLabel(f)} <span className="ht-visually-hidden">in {fieldUnit(f)}</span>
                </label>
                <span className="measure-input">
                  <input id={`m-${f}`} className="ht-input" inputMode="decimal"
                         value={values[f] ?? ""}
                         onChange={(e) => {
                           setValues((v) => ({ ...v, [f]: e.target.value }));
                           setSaid(null);
                         }} />
                  <span aria-hidden="true" className="measure-unit">{fieldUnit(f)}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>

        <footer className="ht-window-foot measure-foot">
          {problem !== null && (
            <p className="ht-callout measure-problem" role="alert">{problem}</p>
          )}
          {said !== null && <p className="ht-callout measure-said" role="status">{said}</p>}
          <div className="measure-actions">
            <button type="button" className="ht-button ht-button-primary"
                    onClick={() => { void save(); }} disabled={saving}>
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </footer>
      </div>
    </dialog>
  );
}
